import { Box } from '@material-ui/core';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Dialog from 'components/Dialog';
import { AppContext } from 'context';
import { ClosureForm, DialogWidth } from 'types';
import Loading from 'components/Loading';
import { ClosureContext } from 'pages/Closures/ClosureClose';
import ConfirmationPopInContent from 'components/Generic/ConfirmationPopInContent';
import { getMaximumInboundStocks, postInventoriedInboundStocks } from 'services/apiOperations';
import { formatInventoriedInboundStocksBeforePost } from 'utils/normalizer';
import {
  InventoriedInboundStockForm,
  InventoriedInboundStockUpdateForm,
} from 'pages/Stocks/Inbound/InventoriedInboundStockForm';
import { notify } from 'components/Notifier';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { FormStepComponentProps } from '../MultiStepForm';
import { postPreviewClosure } from './previewClosure';

const StepInventoriedInboundStock: React.FC<FormStepComponentProps<ClosureForm, ClosureContext>> = ({
  onSubmit,
  formName,
  setFormContext,
  previousStepData,
  formContext,
}) => {
  const { t } = useTranslation();
  const [isOpenPopinConfirmationOneInventoriedStocksZero, setIsOpenPopinConfirmationOneInventoriedStocksZero] =
    useState(false);
  const [isOpenPopinConfirmationInventoriedStocks, setIsOpenPopinConfirmationInventoriedStocks] = useState(false);
  const [inventoryFormData, setInventoryFormData] = useState<InventoriedInboundStockUpdateForm>({ stocks: [] });
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const closure = previousStepData.closure;

  const { handleSubmit } = useForm<ClosureForm>({
    mode: 'onBlur',
  });

  if (!closure) return <Loading />;

  const onSubmitInventoryForm = async (inventory: InventoriedInboundStockUpdateForm) => {
    setInventoryFormData(inventory);

    if (
      inventory.stocks.some(inventoriedInboundStock => {
        return (
          undefined !== inventoriedInboundStock.weight && Math.abs(inventoriedInboundStock.weight) < Number.EPSILON
        );
      })
    ) {
      setIsOpenPopinConfirmationOneInventoriedStocksZero(true);
    } else {
      setIsOpenPopinConfirmationInventoriedStocks(true);
    }
  };

  const generatePreview = async () => {
    if (!closure || !sortingCenter) {
      return;
    }
    const validationViolations = await postPreviewClosure(closure, sortingCenter, t);
    if (!validationViolations) {
      return;
    }
    setFormContext(previousFormContext => ({ ...validationViolations, ...previousFormContext }));
    handleSubmit(onSubmit)();
  };

  const handleValidatePopinConfirmationInventoriedStocks = async () => {
    setIsOpenPopinConfirmationOneInventoriedStocksZero(false);
    setIsOpenPopinConfirmationInventoriedStocks(false);
    if (!sortingCenter) return;

    const sortingCenterId = sortingCenter['@id'];
    const totalInventoriedInboundStocks = inventoryFormData.stocks.reduce(
      (total, inventoriedInboundStocks) =>
        (inventoriedInboundStocks.weight ? inventoriedInboundStocks.weight : 0) + total,
      0,
    );

    const formattedInventoriedInboundStocks = formatInventoriedInboundStocksBeforePost(
      inventoryFormData,
      sortingCenterId,
    );

    try {
      await postInventoriedInboundStocks(formattedInventoriedInboundStocks, sortingCenterId);
      const maximumInboundStocks = await getMaximumInboundStocks(
        Number(transformObjectId(sortingCenter)),
        closure.year,
      );

      if (maximumInboundStocks && totalInventoriedInboundStocks > maximumInboundStocks.maximumInboundStock) {
        notify({
          message: t('closureCloseForm.stepInventoriedInboundStock.maximumInboundStockError', {
            maxRestant: maximumInboundStocks.maximumInboundStock.toString().replace('.', ','),
          }),
          severity: 'error',
        });
      } else {
        generatePreview();
      }
    } catch (e) {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
    }
  };

  return (
    <Box px={8} pt={6}>
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isOpenPopinConfirmationOneInventoriedStocksZero}
        onClose={() => setIsOpenPopinConfirmationOneInventoriedStocksZero(false)}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <ConfirmationPopInContent
            title={t('closures.closureConfirmationPopInOneInventoriedStocksZero.title')}
            cancelText={t('no')}
            validationText={t('yes')}
            cancelAction={() => setIsOpenPopinConfirmationOneInventoriedStocksZero(false)}
            validateAction={handleValidatePopinConfirmationInventoriedStocks}
          />
        )}
      />
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={isOpenPopinConfirmationInventoriedStocks}
        onClose={() => setIsOpenPopinConfirmationInventoriedStocks(false)}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <ConfirmationPopInContent
            title={t('closures.closureConfirmationPopInInventoriedInboundStocks.title')}
            cancelText={t('no')}
            validationText={t('yes')}
            cancelAction={() => setIsOpenPopinConfirmationInventoriedStocks(false)}
            validateAction={handleValidatePopinConfirmationInventoriedStocks}
          />
        )}
      />

      <InventoriedInboundStockForm
        formName={formName}
        onSubmitProp={onSubmitInventoryForm}
        year={(closure.year + 1).toString()}
        formContext={formContext}
        setFormContext={setFormContext}
      />
    </Box>
  );
};

export default StepInventoriedInboundStock;
